import type { Identifier } from "ra-core";

import type { CorretajeSaleInput } from "../../corretaje/corretajeSale";
import type { CorretajeVoboDecision } from "../../types";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic E4 (TASK-026): thin typed wrappers over the operations RPCs
 * (TASK-024/025). The `operations` resource and the `operations_summary` view
 * are read through the standard resource calls; only the actions need an RPC.
 * Every function only forwards: the SECURITY DEFINER functions are the
 * authority (role checks, sellable-project rule, folio, history) and raise
 * Spanish messages that are surfaced as-is.
 */
const rpc = async (
  name: string,
  args: Record<string, unknown>,
): Promise<void> => {
  const { error } = await getSupabaseClient().rpc(name, args);
  if (error) {
    console.error(`${name}.error`, error);
    throw new Error(error.message || "No se pudo completar la acción");
  }
};

export const createSupabaseOperationsMethods = () => ({
  /**
   * `sign_operation()`: sets the contract folio `{PROJ}-{UNIT}-{YYMMDD}`,
   * closes the operation and marks the unit `vendida`. `signedOn` is an ISO
   * date (`YYYY-MM-DD`); it defaults to today on the server.
   */
  signOperation: (operationId: string, signedOn?: string): Promise<void> =>
    rpc("sign_operation", {
      p_operation_id: operationId,
      p_signed_on: signedOn ?? null,
    }),

  /** `cancel_operation()`: the reason is mandatory; releases the reservation. */
  cancelOperation: (operationId: string, reason: string): Promise<void> =>
    rpc("cancel_operation", {
      p_operation_id: operationId,
      p_reason: reason,
    }),

  /** `change_operation_advisor()`: reassigns the operation; the reason is mandatory. */
  changeOperationAdvisor: (
    operationId: string,
    newSalesId: Identifier,
    reason: string,
  ): Promise<void> =>
    rpc("change_operation_advisor", {
      p_operation_id: operationId,
      p_new_sales_id: newSalesId,
      p_reason: reason,
    }),

  /**
   * Almikia Epic EC (TASK-032) `create_corretaje_operation()`: creates the
   * corretaje operation of a DISPONIBLE option for a buyer lead (the option
   * becomes apartada) and returns the new `ALM-COR-{YY}-{NNNNN}` id.
   */
  createCorretajeOperation: async (
    optionId: Identifier,
    dealId: Identifier,
  ): Promise<string> => {
    const { data, error } = await getSupabaseClient().rpc(
      "create_corretaje_operation",
      { p_option_id: optionId, p_deal_id: dealId },
    );
    if (error) {
      console.error("create_corretaje_operation.error", error);
      throw new Error(error.message || "No se pudo completar la acción");
    }
    return data as string;
  },

  /** `save_corretaje_sale()`: the sale administration; the server validates every rule. */
  saveCorretajeSale: (
    operationId: string,
    input: CorretajeSaleInput,
  ): Promise<void> =>
    rpc("save_corretaje_sale", {
      p_operation_id: operationId,
      p_shared_with_colega: input.shared_with_colega,
      p_colega_id: input.colega_id,
      p_colega_found_by: input.colega_found_by,
      p_property_owner: input.property_owner,
      p_commission_total: input.commission_total,
    }),

  /** `request_corretaje_vobo()`: asks Direccion for the VoBo once every required field is complete. */
  requestCorretajeVobo: (operationId: string): Promise<void> =>
    rpc("request_corretaje_vobo", { p_operation_id: operationId }),

  /** `set_corretaje_vobo()`: Direccion approves, observes (note required) or reopens (note required). */
  setCorretajeVobo: (
    operationId: string,
    decision: CorretajeVoboDecision,
    note?: string,
  ): Promise<void> =>
    rpc("set_corretaje_vobo", {
      p_operation_id: operationId,
      p_decision: decision,
      p_note: note ?? null,
    }),
});
